import classNames from "classnames";

import { SUPPRESSED_AUTOCOMPLETE_VALUE } from "./suppress-browser-autocomplete";
import { forwardRef, useEffect, useId, useRef, useState } from "react";
import { FieldError } from "react-hook-form";

export interface MultiSelectOption {
  label: string;
  value: string;
  isChecked: boolean;
}

interface MultiSelectFieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  label: string; // This is the field label (e.g., "Subject / Test")
  selectedDisplayText?: string; // This is what shows inside the dropdown. e.g., "Math, Science"
  options: MultiSelectOption[];
  isRequired?: boolean;
  validationState?: "success" | "error";
  disableColClass?: boolean;
  error?: FieldError;
  disabled?: boolean;
  onChange?: (option: MultiSelectOption) => void;
  optionLabels?: Record<string, string>; // Optional custom labels for options
}

export const MultiSelectField = forwardRef<HTMLDivElement, MultiSelectFieldProps>(
  (
    {
      label,
      selectedDisplayText,
      options,
      validationState,
      isRequired = false,
      disableColClass = false,
      error,
      disabled = false,
      onChange,
      optionLabels,
      className,
      ...divProps
    },
    ref
  ) => {
    const id = useId();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleCheckboxChange = (option: MultiSelectOption, event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        option.isChecked = event.target.checked;
        onChange(option);
      }
    };

    const toggleDropdown = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div className={classNames("form-floating", { col: !disableColClass })} ref={ref} {...divProps}>
        <input
          type="text"
          id={id}
          className={classNames("form-select multi-select-field", className, {
            "is-invalid": validationState === "error" || error,
            "is-valid": validationState === "success"
          })}
          value={selectedDisplayText || ""}
          style={{
            cursor: disabled ? "default" : "pointer",
            caretColor: "transparent"
          }}
          onClick={toggleDropdown}
          onChange={() => {
            /* Prevent typing */
          }}
          onKeyDown={e => {
            e.preventDefault(); // Prevent all keyboard input
            if (e.key === "Enter" || e.key === " ") {
              toggleDropdown();
            }
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${id}-listbox`}
          disabled={disabled}
          required={isRequired}
          autoComplete={SUPPRESSED_AUTOCOMPLETE_VALUE}
        />
        <div className="position-relative" ref={dropdownRef}>
          {isOpen && (
            <div
              id={`${id}-listbox`}
              className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
              style={{
                top: "100%",
                zIndex: 1050,
                maxHeight: "250px",
                overflowY: "auto"
              }}
              role="listbox"
            >
              <div className="p-2">
                {options.map(option => (
                  <div key={option.value} className="d-flex align-items-center mb-2 last:mb-0">
                    <div className="form-check d-flex align-items-center w-100">
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id={option.value}
                        checked={option.isChecked}
                        onChange={e => handleCheckboxChange(option, e)}
                        disabled={disabled}
                      />
                      <div className="border-end me-3" style={{ height: "25px", borderColor: "#dee2e6" }} />
                      <label className="form-check-label mb-0 flex-grow-1" htmlFor={option.value}>
                        {option.label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <label htmlFor={id} className="text-nowrap">
          {label}
          {isRequired && (
            <>
              {" "}
              <span className="required" aria-hidden="true">
                *
              </span>
            </>
          )}
        </label>
      </div>
    );
  }
);

MultiSelectField.displayName = "MultiSelectField";
